import { useMemo, useRef } from "react";
import type { PenStrokeCanvasObject } from "@refinement-canvas/domain";
import { buildStrokePath } from "../../../lib/pen-stroke";
import { useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";

interface Props {
  readonly object: PenStrokeCanvasObject;
}

// 確定済みのペンストローク。通常オブジェクトと同じ zOrder で描画し、後から作ったものが上に重なる。
export function PenStrokeObject({ object }: Props): JSX.Element {
  const ref = useRef<SVGSVGElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  const isSelected = useCanvasStore((s) =>
    s.selectedObjectIds.length === 1 && s.selectedObjectIds[0] === object.id
  );
  const path = useMemo(
    () => buildStrokePath(object.points, { thickness: object.thickness }),
    [object.points, object.thickness]
  );

  useObjectDrag({
    objectId: object.id,
    elementRef: ref as unknown as React.RefObject<HTMLElement | null>,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  return (
    <svg
      ref={ref}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="pen-stroke"
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        overflow: "visible",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        cursor: "move",
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
        outline: isSelected ? "2px solid var(--color-accent)" : "none",
      }}
      viewBox={`${object.x} ${object.y} ${object.w} ${object.h}`}
    >
      <path d={path} fill={object.color} pointerEvents="painted" />
    </svg>
  );
}
